import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AddTask as AddTaskIcon,
  NoteAdd as NoteAddIcon,
} from "@mui/icons-material";
import {
  formatDate,
  formatCurrency,
  getClientStatusDisplay,
  getLeadStatusDisplay,
  getProjectStatusDisplay,
  getTaskStatusDisplay,
} from "../utils/crmHelpers";

const CRMManager = ({
  mode = "client",
  record,
  tasks = [],
  interactions = [],
  onEdit,
  onDelete,
  onCreateTask,
  onCreateInteraction,
  onConvertLead,
}) => {
  const [tab, setTab] = useState(0);

  if (!record) return null;

  const getStatusDisplay = () => {
    if (mode === "client") return getClientStatusDisplay(record.status);
    if (mode === "lead") return getLeadStatusDisplay(record.status);
    if (mode === "project") return getProjectStatusDisplay(record.status);
    return { label: record.status, color: "default" };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {record.fullName || record.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {record.clientCode ||
                record.leadCode ||
                record.projectCode ||
                record.email ||
                ""}
            </Typography>
          </Box>

          <Chip label={statusDisplay.label} color={statusDisplay.color} />
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
          <Button startIcon={<EditIcon />} variant="outlined" onClick={onEdit}>
            Edit
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            variant="outlined"
            color="error"
            onClick={onDelete}
          >
            Delete
          </Button>
          <Button
            startIcon={<AddTaskIcon />}
            variant="outlined"
            onClick={onCreateTask}
          >
            Add Task
          </Button>
          <Button
            startIcon={<NoteAddIcon />}
            variant="outlined"
            onClick={onCreateInteraction}
          >
            Log Interaction
          </Button>
          {mode === "lead" && (
            <Button variant="contained" color="success" onClick={onConvertLead}>
              Convert to Client
            </Button>
          )}
        </Stack>
      </Paper>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="scrollable"
      >
        <Tab label="Overview" />
        <Tab label="Tasks" />
        <Tab label="Interactions" />
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {mode !== "project" && (
              <>
                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Email</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{record.email || "N/A"}</Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Phone</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{record.phone || "N/A"}</Typography>
                </Grid>
              </>
            )}

            {mode === "client" && (
              <>
                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Type</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{record.type || "N/A"}</Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Company</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{record.company || "N/A"}</Typography>
                </Grid>
              </>
            )}

            {mode === "lead" && (
              <>
                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Lead Type</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{record.leadType || "N/A"}</Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Budget</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>
                    {formatCurrency(record.requirements?.budget || 0)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Event Date</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>
                    {formatDate(record.requirements?.eventDate)}
                  </Typography>
                </Grid>
              </>
            )}

            {mode === "project" && (
              <>
                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Client ID</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{record.clientId || "N/A"}</Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Type</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{record.type || "N/A"}</Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Event Date</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{formatDate(record.event?.mainDate)}</Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Venue</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>{record.event?.venue?.name || "N/A"}</Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography color="text.secondary">Package Price</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography>
                    {formatCurrency(record.package?.price || 0)}
                  </Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography color="text.secondary">Notes</Typography>
              <Typography sx={{ mt: 1 }}>
                {record.notesSummary || record.message || "No notes available."}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Tasks
          </Typography>
          {tasks.length === 0 ? (
            <Typography color="text.secondary">No tasks found.</Typography>
          ) : (
            <List>
              {tasks.map((task) => {
                const taskStatus = getTaskStatusDisplay(task.status);
                return (
                  <ListItem key={task.id} divider>
                    <ListItemText
                      primary={task.title}
                      secondary={`${task.priority} • Due: ${formatDate(task.dueDate)}`}
                    />
                    <Chip
                      label={taskStatus.label}
                      color={taskStatus.color}
                      size="small"
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Interactions
          </Typography>
          {interactions.length === 0 ? (
            <Typography color="text.secondary">
              No interactions found.
            </Typography>
          ) : (
            <List>
              {interactions.map((item) => (
                <ListItem key={item.id} divider>
                  <ListItemText
                    primary={`${item.type} • ${item.title || "Interaction"}`}
                    secondary={`${formatDate(item.date)} • ${item.description || item.notes || ""}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CRMManager;
